import { Box, Button, CardActions, Grid, Pagination, Stack, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { ApiError } from "../../Api/ApiError";
import { SearchCustomer, SearchCustomerProps } from "../../Api/Customer";
import { CustomerInterface } from "../../Objects/Customer";
import PageTitle from "../atoms/PageTitle";
import CustomerCard from "../molecules/CustomerCard";
import CustomerSearchForm from "../molecules/CustomerSearchForm";
import { CustomerDataEditDialog } from "../organisms/CustomerDataEditDialog";
import CustomerDetailDialog from "../organisms/CustomerDetailDialog";

const CustomerPage: React.FC = () => {
    let { enqueueSnackbar } = useSnackbar()
    const defSearchCustomerProps = {
        searchStr: "",
        page: 1,
        num: 18,
        accessKey: "HEAD-OFFICE_LOGIN"
    }
    const [searchStrings, setSearchStrings] = React.useState<string>("")
    const [searchCustomerProps, setSearchCustomerProps] = React.useState<SearchCustomerProps>(defSearchCustomerProps)

    const [customerList, setCustomerList] = React.useState<CustomerInterface[]>()
    const [rows, setRows] = React.useState<number>()

    const [detailCustomerId, setDetailCustomerId] = React.useState<number>()
    const [createCustomerDialog, setCreateCustomerDialog] = React.useState<boolean>(false)

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const loadCustomerList = React.useCallback(() => {
        if (searchCustomerProps.searchStr) {
            SearchCustomer(searchCustomerProps, {signal: abortController.signal}).then(result => {
                setRows(result.rows)
                setCustomerList(result.list)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, searchCustomerProps, enqueueSnackbar])

    React.useEffect(() => {
        loadCustomerList()
    }, [loadCustomerList])

    return (
        <>
            <PageTitle text="顧客管理" />
            <Stack
                spacing={2}
            >
                <Box>
                    <Stack
                        direction={"row"}
                        justifyContent="right"
                    >
                        <Button
                            variant="contained"
                            onClick={() => {
                                setCreateCustomerDialog(true)
                            }}
                        >新規顧客登録</Button>
                    </Stack>
                </Box>
                <Box
                    maxWidth={800}
                >
                    <CustomerSearchForm
                        searchStrings={searchStrings}
                        onSearch={() => {
                            setSearchCustomerProps( prev => {
                                return {
                                    ...prev,
                                    page: 1,
                                    searchStr: searchStrings
                                }
                            })
                        }}
                        onChange={(e) => {
                            setSearchStrings(e.target.value)
                        }}
                    />
                </Box>
                <Box>
                    {customerList === undefined ? (
                        <>
                            <Typography>上のボックスから検索を開始してください。</Typography>
                            <Typography>バーコード、名前、よみがな、電話番号、E-Mailから検索できます。</Typography>
                        </>
                    ) : (
                        customerList.length > 0 ?
                            (
                                <>
                                    {rows &&
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "right",
                                                alignItems: "center",
                                                mb: 2
                                            }}
                                        >
                                            <Typography mr={2}>全{rows}件</Typography>
                                            <Pagination
                                                count={Math.ceil(rows / searchCustomerProps.num)}
                                                page={searchCustomerProps.page}
                                                variant="outlined"
                                                onChange={(e, page) => {
                                                    setSearchCustomerProps( prev => {
                                                        return {
                                                            ...prev,
                                                            page: page
                                                        }
                                                    })
                                                }}
                                            />
                                        </Box>
                                    }
                                    <Grid container spacing={2}>
                                        {customerList.map((customer, index) => (
                                            <Grid item key={`${index}-${customer.ctm_id}`} xs={12} sm={6} md={4}>
                                                <CustomerCard
                                                    key={customer.ctm_id}
                                                    customer={customer}
                                                    footMenu={<CardActions
                                                        sx={{ justifyContent: "right" }}
                                                    >
                                                        <Button
                                                            variant="outlined"
                                                            onClick={() => {
                                                                setDetailCustomerId(customer.ctm_id)
                                                            }}
                                                        >詳細を開く</Button>
                                                    </CardActions>}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </>
                            )
                            :
                            <Typography>データが見つかりません</Typography>
                    )}
                </Box>
            </Stack>
            <CustomerDetailDialog
                ctmId={detailCustomerId}
                onClose={() => {
                    loadCustomerList()
                    setDetailCustomerId(undefined)
                }}
            />
            <CustomerDataEditDialog
                ctmId={createCustomerDialog ? 0 : undefined}
                onClose={() => {
                    loadCustomerList()
                    setCreateCustomerDialog(false)
                }}
                onUpdate={(ctm_id) => {
                    setDetailCustomerId(ctm_id)
                }}
            />
        </>
    )
}

export default CustomerPage